import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import {
  nextWorkspacePaneSidebarOpen,
  WORKSPACE_PANE_SIDEBAR_EVENT,
  type WorkspacePaneSidebarAction,
} from '@affine/core/modules/streamlain/workspace-pane';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo } from 'react';

/** Local visibility, scoped to the same account/workspace as the folder tree. */
export function useWorkspaceSidebarVisibility(
  surface: 'all' | 'overlay',
  listenToRequests = surface === 'all'
) {
  const navigation = useService(NavigationPanelService);
  const path = useMemo(() => ['workspaceDocuments', surface], [surface]);
  const open = !useLiveData(navigation.collapsed$(path, surface === 'overlay'));
  const setOpen = useCallback(
    (value: boolean) => navigation.setCollapsed(path, !value),
    [navigation, path]
  );

  useEffect(() => {
    if (!listenToRequests) return;
    const onRequest = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          target?: string;
          action?: WorkspacePaneSidebarAction;
        }>
      ).detail;
      if (detail?.target !== 'workspace') return;
      const currentOpen = !navigation.collapsed$(path).value;
      setOpen(nextWorkspacePaneSidebarOpen(detail.action, currentOpen));
    };
    window.addEventListener(WORKSPACE_PANE_SIDEBAR_EVENT, onRequest);
    return () =>
      window.removeEventListener(WORKSPACE_PANE_SIDEBAR_EVENT, onRequest);
  }, [listenToRequests, navigation, path, setOpen]);

  return [open, setOpen] as const;
}
